/**
 * Returns ingredient text as HTML with allergen words in bold, for every
 * language.
 *
 * The catalogue sync now ships ingredient declarations pre-rendered as safe
 * HTML — allergen words wrapped in `<b>` tags, all other content XML-escaped —
 * identically for EN/FR/ES (FR/ES via DeepL `tag_handling=xml`, which preserves
 * the `<b>` tags through translation). When we detect that pre-rendered HTML we
 * return it as-is.
 *
 * Legacy fallback: older snapshots (and any data still using the raw MS-Access
 * `<ALLERGEN#` markers) are handled the old way — strip the markers and bold
 * recognised allergen keywords — so nothing breaks during a sync transition.
 */

const ALLERGEN_KEYWORDS = [
  'WHEAT', 'MILK', 'EGG', 'EGGS', 'ALMOND', 'ALMONDS', 'HAZELNUT', 'HAZELNUTS',
  'SOYA', 'GLUTEN', 'BARLEY', 'OAT', 'OATS', 'RYE', 'SPELT', 'KAMUT',
  'CASHEW', 'CASHEWS', 'PECAN', 'PECANS', 'WALNUT', 'WALNUTS',
  'PISTACHIO', 'PISTACHIOS', 'MACADAMIA', 'BRAZIL',
  'PEANUT', 'PEANUTS', 'FISH', 'CRUSTACEAN', 'CRUSTACEANS',
  'MOLLUSC', 'MOLLUSCS', 'CELERY', 'MUSTARD', 'SESAME',
  'SULPHITE', 'SULPHITES', 'SULPHUR', 'LUPIN', 'LUPINS',
  'SHELLFISH', 'NUT', 'NUTS',
];

export function boldAllergens(text: string): string {
  if (!text) return '';

  // New pipeline: backend ships pre-bolded, XML-escaped HTML (<b> tags +
  // &amp;/&lt;/&gt; entities). Trust and render it verbatim.
  if (/<\/?b>/i.test(text) || /&(?:amp|lt|gt);/.test(text)) {
    return text;
  }

  // Legacy fallback: raw <ALLERGEN#> markers from older snapshots.
  let cleaned = text.replace(/<([A-Z]{2,})#/g, '$1');
  cleaned = cleaned.replace(/<([A-Z])/g, '$1').replace(/([A-Z])#/g, '$1');
  return cleaned.replace(/\b([A-Z]{2,})\b/g, match => {
    if (ALLERGEN_KEYWORDS.includes(match)) return `<strong>${match}</strong>`;
    return match;
  });
}
